import { inject, Injectable, signal } from '@angular/core';
import { IconModel } from '@models/icon';
import { BehaviorSubject, catchError, EMPTY, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { Domain, DomainDto, Domains, Subdomain, SubdomainDto } from '../../models/domain';
import { DomainService } from './domain.service';
import { SubdomainService } from './subdomain.service';

/**
 * A stateful Facade that holds and manages
 * the global filter state. It uses the API services to fetch/mutate
 * data and updates its internal state.
 */
@Injectable({
  providedIn: 'root',
})
export class DomainFacade {
  // --- State ---
  private readonly domains$ = new BehaviorSubject<Domains>([]);
  isLoading = signal(true);

  // --- Injected Services ---
  private readonly domainService = inject(DomainService);
  private readonly subdomainService = inject(SubdomainService);

  // --- Public Observable (Getter) ---

  getDomains(): Observable<Domains> {
    return this.domains$.asObservable();
  }

  /**
   * Will work only on initiated domain list
   *
   * @param id Domain id
   * @returns Domain by id, undefined if not found
   */
  getDomainById(id: number): Domain | undefined {
    return (this.domains$.value || []).find((v) => v.id === id);
  }

  setDomains(): void {
    this.isLoading.set(true);
    this.domainService.getDomains().subscribe({
      next: (domains) => {
        this.isLoading.set(false);
        // 1. Sort domains by the (potentially 1-based) sort property
        const sortedDomains = [...domains].sort((a, b) => a.sort - b.sort);

        // 2. Re-normalize the list to be 0-based
        const normalizedDomains = sortedDomains.map((domain, index) => {
          const sortedSubdomains = [...domain.subdomains].sort((a, b) => a.sort - b.sort);
          const normalizedSubdomains = sortedSubdomains.map((sub, subIndex) => ({
            ...sub,
            sort: subIndex,
          }));

          return {
            ...domain,
            sort: index,
            subdomains: normalizedSubdomains,
          };
        });

        this.domains$.next(normalizedDomains);
      },
      error: (err) => console.error('failed to fetch operations', err),
    });
  }

  addDomain(addDto: DomainDto): void {
    this.domainService
      .addDomain(addDto)
      .pipe(
        switchMap((newDomain) => {
          const currentDomains = this.domains$.getValue();
          const updatedDomains = [...currentDomains];

          updatedDomains.splice(addDto.sort, 0, newDomain);

          updatedDomains.forEach((domain, index) => (domain.sort = index));

          this.domains$.next(updatedDomains);

          const domainIds = updatedDomains.map((domain) => domain.id);

          return this.domainService.updateDomainsOrder(domainIds).pipe(
            catchError((sortErr) => {
              console.error('Failed to update domain order after add:', sortErr);
              this.domains$.next(currentDomains);
              return throwError(() => sortErr);
            }),
          );
        }),
        catchError((addErr) => {
          console.error('Failed to add domain:', addErr);
          return of(null);
        }),
      )
      .subscribe();
  }

  editDomain(domainId: number, updateDto: DomainDto): void {
    // 1. Get original state for rollback
    const originalDomains = this.domains$.getValue();
    const fromIndex = originalDomains.findIndex((d) => d.id === domainId);
    if (fromIndex === -1) return;

    const targetIndex = updateDto.sort ?? fromIndex;
    const needsReorder = targetIndex !== fromIndex;

    let optimisticallyUpdatedDomains: Domains;

    if (needsReorder) {
      // --- REORDER + EDIT ---
      optimisticallyUpdatedDomains = [...originalDomains];

      // a. Remove item
      const [itemToMove] = optimisticallyUpdatedDomains.splice(fromIndex, 1);

      const newInsertIndex = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;

      // c. Re-insert item at the correct, adjusted index
      optimisticallyUpdatedDomains.splice(newInsertIndex, 0, itemToMove);

      // d. Re-normalize sort properties AND update the edited item's data
      optimisticallyUpdatedDomains = optimisticallyUpdatedDomains.map((domain, index) => {
        const updatedDomain = { ...domain, sort: index };

        // e. Check against the newInsertIndex
        if (index === newInsertIndex) {
          // This is the item we moved
          return {
            ...updatedDomain,
            name: updateDto.name ?? updatedDomain.name,
            icon: updateDto.icon ?? updatedDomain.icon,
            homepageImage: updateDto.homepageImage ?? updatedDomain.homepageImage,
          };
        }
        return updatedDomain;
      });
    } else {
      // --- SIMPLE EDIT (no reorder) ---
      optimisticallyUpdatedDomains = originalDomains.map((domain) => {
        if (domain.id === domainId) {
          const { sort, ...dtoData } = updateDto;
          return { ...domain, ...dtoData };
        }
        return domain;
      });
    }

    // 4. Optimistic update
    this.domains$.next(optimisticallyUpdatedDomains);

    // 5. Prepare DTO for the API call
    const dataUpdateDto: DomainDto = {
      name: updateDto.name,
      icon: updateDto.icon,
      description: updateDto.description,
      homepageImage: updateDto.homepageImage,
      sort: targetIndex,
    };

    this.domainService
      .editDomain(domainId, dataUpdateDto)
      .pipe(
        switchMap((updatedDomainFromServer) => {
          const finalizedDomains = optimisticallyUpdatedDomains.map((domain) =>
            domain.id === domainId ? { ...domain, ...updatedDomainFromServer, sort: domain.sort } : domain,
          );
          this.domains$.next(finalizedDomains);

          if (needsReorder) {
            const domainIds = finalizedDomains.map((d) => d.id);
            return this.domainService.updateDomainsOrder(domainIds).pipe(
              catchError((sortErr) => {
                console.error('Failed to update domain order after edit, rolling back...', sortErr);
                this.domains$.next(originalDomains);
                return throwError(() => sortErr);
              }),
            );
          } else {
            return of(updatedDomainFromServer);
          }
        }),
        catchError((editErr) => {
          console.error('Failed to edit domain, rolling back...', editErr);
          this.domains$.next(originalDomains);
          return of(EMPTY);
        }),
      )
      .subscribe();
  }

  deleteDomain(domainId: number): Observable<any> {
    return this.domainService.deleteDomain(domainId).pipe(
      tap(() => {
        const updatedDomains = this.domains$.getValue().filter((op) => op.id !== domainId);
        this.domains$.next(updatedDomains);
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  updateDomainsOrder(domains: Domains): void {
    const oldState = this.domains$.getValue();

    this.domains$.next(domains);

    const domainIds = domains.map((domain) => domain.id);

    this.domainService
      .updateDomainsOrder(domainIds)
      .pipe(
        catchError((err) => {
          this.domains$.next(oldState);
          console.error('Failed to save new domain order:', err);
          return throwError(() => err);
        }),
      )
      .subscribe();
  }

  addSubdomain(domainId: number, subdomainDto: SubdomainDto): void {
    this.subdomainService
      .addSubdomain(domainId, subdomainDto)
      .pipe(
        switchMap((newSubdomain) => {
          // 2. Get the current state
          const currentDomains = this.domains$.getValue();
          let updatedSubdomainIds: number[] = [];

          // 3. Optimistically update the state
          const updatedDomains = currentDomains.map((domain) => {
            if (domain.id !== domainId) {
              return domain;
            }

            // a. Insert the new subdomain
            const updatedSubdomains = [...domain.subdomains];
            updatedSubdomains.splice(subdomainDto.sort, 0, newSubdomain);

            // b. Re-normalize the sort property for all subdomains
            const finalizedSubdomains = updatedSubdomains.map((subdomain, index) => ({
              ...subdomain,
              sort: index,
            }));

            // c. Get the IDs for the sorting API call
            updatedSubdomainIds = finalizedSubdomains.map((sub) => sub.id);

            // d. Return the updated domain object
            return {
              ...domain,
              subdomains: finalizedSubdomains,
            };
          });

          // 4. Broadcast the new state (UI updates instantly)
          this.domains$.next(updatedDomains);

          // 5. Chain the call to the sorting API
          return this.subdomainService.updateSubdomainsOrder(domainId, updatedSubdomainIds).pipe(
            catchError((sortErr) => {
              // 5a. Handle SORT error: Roll back state
              console.error('Failed to update subdomain order after add, rolling back...', sortErr);
              this.domains$.next(currentDomains); // Revert to original state
              return throwError(() => sortErr);
            }),
          );
        }),
        catchError((addErr) => {
          // 6. Handle ADD error: Log and stop the chain
          console.error(`Failed to add subdomain to domain ${domainId}:`, addErr);
          return of(null); // Stop the observable chain
        }),
      )
      .subscribe();
  }

  deleteSubdomain(domainId: number, subdomainId: number): Observable<any> {
    return this.subdomainService.deleteSubdomain(domainId, subdomainId).pipe(
      tap(() => {
        const domains = this.domains$.getValue();
        const updatedOperations = domains.map((d) => {
          if (d.id === domainId) {
            d.subdomains = d.subdomains.filter((sub) => sub.id !== subdomainId);
          }
          return d;
        });
        this.domains$.next(updatedOperations);
      }),
      catchError((error) => {
        console.error(`Failed to delete subdomain ${subdomainId}:`, error);
        return throwError(() => error);
      }),
    );
  }

  editSubdomain(domainId: number, subdomainId: number, updateDto: SubdomainDto): void {
    // 1. Get original state for rollback
    const originalDomains = this.domains$.getValue();

    // 2. Find indices
    const domainIndex = originalDomains.findIndex((d) => d.id === domainId);
    if (domainIndex === -1) return;

    const originalDomain = originalDomains[domainIndex];
    const fromIndex = originalDomain.subdomains.findIndex((s) => s.id === subdomainId);
    if (fromIndex === -1) return;

    // 3. Get 0-based target index
    const targetIndex = updateDto.sort ?? fromIndex;
    const needsReorder = targetIndex !== fromIndex;

    let optimisticallyUpdatedDomains: Domains;

    // 4. Create optimistic state
    if (needsReorder) {
      // --- REORDER + EDIT Subdomain ---
      const reorderedSubdomains = [...originalDomain.subdomains];

      // a. Remove item
      const [itemToMove] = reorderedSubdomains.splice(fromIndex, 1);

      // b. Calculate the *actual* insertion index
      const newInsertIndex = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;

      // c. Re-insert item at the correct, adjusted index
      reorderedSubdomains.splice(newInsertIndex, 0, itemToMove);

      // d. Map and update sort + data
      const finalizedSubdomains = reorderedSubdomains.map((sub, index) => {
        const updatedSub = { ...sub, sort: index };

        // e. Check against newInsertIndex
        if (index === newInsertIndex) {
          // This is the item we moved
          return {
            ...updatedSub,
            name: updateDto.name ?? updatedSub.name,
            icon: updateDto.icon ?? updatedSub.icon,
          };
        }
        return updatedSub;
      });

      // f. Create new domain object
      const updatedDomain = { ...originalDomain, subdomains: finalizedSubdomains };

      // g. Create new domains array
      optimisticallyUpdatedDomains = originalDomains.map((d, i) => (i === domainIndex ? updatedDomain : d));
    } else {
      // --- SIMPLE EDIT Subdomain ---
      optimisticallyUpdatedDomains = originalDomains.map((d) => {
        if (d.id !== domainId) return d;
        const updatedSubdomains = d.subdomains.map((s) => {
          if (s.id === subdomainId) {
            const { sort, ...dtoData } = updateDto;
            return { ...s, ...dtoData };
          }
          return s;
        });
        return { ...d, subdomains: updatedSubdomains };
      });
    }

    // 5. Optimistic update
    this.domains$.next(optimisticallyUpdatedDomains);

    // 6. Prepare DTO for the API call
    const dataUpdateDto: SubdomainDto = {
      name: updateDto.name,
      icon: updateDto.icon,
      sort: targetIndex,
    };

    // 7. API Call Chain
    this.subdomainService
      .editSubdomain(domainId, subdomainId, dataUpdateDto)
      .pipe(
        switchMap((updatedSubdomainFromServer) => {
          const currentOptimisticState = this.domains$.getValue();
          const finalizedDomains = currentOptimisticState.map((domain) => {
            if (domain.id !== domainId) return domain;

            const updatedSubdomains = domain.subdomains.map((sub) =>
              sub.id === subdomainId ? { ...sub, ...updatedSubdomainFromServer, sort: sub.sort } : sub,
            );
            return { ...domain, subdomains: updatedSubdomains };
          });

          this.domains$.next(finalizedDomains);

          if (needsReorder) {
            const subdomainIds = finalizedDomains.find((d) => d.id === domainId)!.subdomains.map((s) => s.id);

            return this.subdomainService.updateSubdomainsOrder(domainId, subdomainIds).pipe(
              catchError((sortErr) => {
                console.error('Failed to update subdomain order after edit, rolling back...', sortErr);
                this.domains$.next(originalDomains);
                return of(EMPTY);
              }),
            );
          } else {
            return of(updatedSubdomainFromServer);
          }
        }),
        catchError((editErr) => {
          console.error(`Failed to edit subdomain ${subdomainId}, rolling back...`, editErr);
          this.domains$.next(originalDomains);
          return of(EMPTY);
        }),
      )
      .subscribe();
  }

  updateSubdomainsOrder(domainId: number, subdomains: Subdomain[]): void {
    const currentDomains = this.domains$.getValue();

    const updatedDomains = currentDomains.map((domain) => {
      if (domain.id !== domainId) {
        return domain;
      }
      return {
        ...domain,
        subdomains: subdomains,
      };
    });
    this.domains$.next(updatedDomains);

    const subdomainIds = subdomains.map((subdomain) => subdomain.id);

    this.subdomainService.updateSubdomainsOrder(domainId, subdomainIds).subscribe({
      error: (err) => {
        console.error('Failed to update subdomain order:', err);
        this.setDomains();
      },
    });
  }
  /**
   * Finds a domain icon by its name from the current state.
   */
  getIconByDomainName(name: string): IconModel {
    const domains = this.domains$.getValue();
    const domain = domains.find((d) => d.name === name);
    return domain?.icon || { name: 'unknown', type: 'airbus' };
  }
}
